<!-- Карточка проблемы: состояние, результат устранения, вложения и комментарии. -->
<template>
  <RecordListDetailsCard :embedded="embedded"
    :title="remark.name" :description="remark.text">
    <template #actions>
      <Actions v-if="!readonly" :is-submitting="store.isSubmitting" @edit="$emit('edit')" />
    </template>
    <template #badges>
      <BaseStatusSelect :value="remark.status" :label="currentStatus.label" :color="currentStatus.color"
        :options="readonly || store.isSubmitting ? [] : statusOptions" @change="$emit('transition', $event)" />
      <Badges :remark="remark" :show-status="false" />
    </template>
    <template #meta><AuthorLine :author="remark.created_by" :created-at="remark.created_at" /></template>
    <BaseFormField for-id="vehicle-remark-details-files" wide>
      <AttachmentViewer
        v-if="readonly"
        :attachments="remark.attachments || []"
      />
      <AttachmentUploader
        v-else
        :key="remark.id"
        :attachment-ids="attachmentIds"
        attachable-type="Fleet::Vehicle::Remark"
        requisite-code="vehicle-remark-details"
        :existing-attachments="remark.attachments"
        @update:attachment-ids="updateAttachments"
      />
    </BaseFormField>
    <section
      v-if="isBlocking"
      class="vehicle-remark-details__warning"
    >
      <AppIcon name="info" :size="20" />
      <div>
        <strong>Препятствует выезду</strong>
        <p>
          Устраните проблему и зафиксируйте результат до следующего
          выезда.
        </p>
      </div>
    </section>
    <section
      v-if="remark.status === 'resolved'"
      class="vehicle-remark-details__resolution"
    >
      <AppIcon name="check" :size="20" />
      <div class="vehicle-remark-details__resolution-body">
        <div class="vehicle-remark-details__resolution-header">
          <strong>Исправлено</strong>
          <time v-if="remark.resolved_at" :datetime="remark.resolved_at">{{ formatDate(remark.resolved_at) }}</time>
          <span v-else>Дата исправления неизвестна</span>
        </div>
        <p v-if="remark.resolution_comment">{{ remark.resolution_comment }}</p>
        <footer v-if="remark.resolved_by" class="vehicle-remark-details__resolution-author">
          <span>Зафиксировал</span>
          <span class="vehicle-remark-details__resolution-author-name">{{ remark.resolved_by.initials }}</span>
        </footer>
      </div>
    </section>
    <FleetRepairRemarkLinks :records="remark.repairs || []" resource="repairs" :vehicle-id="remark.vehicle_id" />
    <BaseFormSection v-if="remark.inspection" title="Акт приемки-передачи" columns="1">
      <BaseRelatedRecordCard
        :title="`Акт № ${remark.inspection.number}`"
        icon="document"
        :to="{ name: 'VehicleDetails', params: { id: remark.vehicle_id }, query: { tab: 'inspections', inspection_id: remark.inspection.id } }"
      />
    </BaseFormSection>
    <RecordListStatusHistory :key="`status-${remark.id}`" :transitions="remark.status_transitions || []" :statuses="statusOptions" />
    <Comments :key="`comments-${remark.id}`" :remark="remark" />
  </RecordListDetailsCard>
</template>
<script>
import FleetRepairRemarkLinks from "@/components/Fleet/RepairRemark/Links.vue";
import RecordListStatusHistory from "@/components/RecordList/StatusHistory.vue";
import remarkFilters from "@/config/filters/vehicle/remarks.json";
import RecordListDetailsCard from "@/components/RecordList/DetailsCard.vue";
import AppIcon from "@/components/Icon/AppIcon.vue";
import { formatDateAndTimeLocal } from "@/utils/dateUtils";
import AttachmentUploader from "@/components/Attachment/Uploader.vue";
import AttachmentViewer from "@/components/Attachment/Viewer.vue";
import { useFleetVehicleRemarkStore } from "@/stores/fleetVehicleRemarkStore";
import Comments from "@/components/Vehicle/Remark/Comments/Discussion.vue";
import AuthorLine from "@/components/Vehicle/Remark/Details/AuthorLine.vue";
import Actions from "@/components/Vehicle/Remark/Details/Actions.vue";
import Badges from "@/components/Vehicle/Remark/Shared/Badges.vue";
export default {
  name: "VehicleRemarkDetailsCard",
  components: { FleetRepairRemarkLinks, RecordListStatusHistory, RecordListDetailsCard, AppIcon, AttachmentUploader, AttachmentViewer, Comments, Badges, Actions, AuthorLine },
  props: {
    remark: { type: Object, required: true },
    embedded: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  emits: ["edit", "transition"],
  data() {
    return { attachmentIds: (this.remark.attachments || []).map((file) => file.id), attachmentSave: Promise.resolve() };
  },
  watch: {
    "remark.id"() { this.attachmentIds = (this.remark.attachments || []).map((file) => file.id); },
  },
  computed: {
    currentStatus() { return this.statusOptions.find((option) => option.value === this.remark.status) || {}; },
    statusOptions() { return remarkFilters.find((field) => field.value === "status").options; },
    isBlocking() {
      return ["open", "in_progress"].includes(this.remark.status) && this.remark.remark_type === "defect" && this.remark.severity === "blocking";
    },
    store() {
      return useFleetVehicleRemarkStore();
    },

  },
  methods: {
    formatDate: formatDateAndTimeLocal,
    updateAttachments(attachmentIds) {
      this.attachmentIds = attachmentIds;
      return this.saveAttachments();
    },
    saveAttachments() {
      const vehicleId = this.remark.vehicle_id;
      const remarkId = this.remark.id;
      const attachmentIds = [...this.attachmentIds];
      // Загрузки завершаются параллельно; обновления набора файлов отправляем последовательно.
      this.attachmentSave = this.attachmentSave.catch(() => {}).then(() =>
        this.store.updateRemark(vehicleId, remarkId, { attachment_ids: attachmentIds })
      );
      return this.attachmentSave;
    },
  },
};
</script>
<style scoped>
.vehicle-remark-details__warning p,
.vehicle-remark-details__resolution p {
  line-height: 1.6;
  font-size: 14px;
  color: #64748b;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.vehicle-remark-details__warning,
.vehicle-remark-details__resolution {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #fff5f3;
  color: #b43434;
  font-size: 13px;
  line-height: 1.5;
}
.vehicle-remark-details__warning svg,
.vehicle-remark-details__resolution svg {
  flex-shrink: 0;
}
.vehicle-remark-details__warning p {
  font-size: 12px;
  color: #a85850;
  margin-top: 4px;
}
.vehicle-remark-details__resolution {
  background: #edf9f3;
  color: #147653;
}
.vehicle-remark-details__resolution-body {
  flex: 1;
  min-width: 0;
}
.vehicle-remark-details__resolution-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 8px;
}
.vehicle-remark-details__resolution-header time,
.vehicle-remark-details__resolution-header > span {
  color: #64748b;
  font-size: 12px;
}
.vehicle-remark-details__resolution-author {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #d5e9de;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.vehicle-remark-details__resolution-author-name {
  color: #476459;
  font-weight: 500;
}

</style>
